// src/cockpit4/components/ProjectRetrospective.tsx
// ============================================
// 项目复盘与持续改进（项目中心 P2-功能10）- v2.0 详细改进计划
// 项目复盘自动触发 / AI 复盘报告 / 经验教训沉淀 / 模板最佳实践 / 效果追踪
// 数据来源：localStorage（xeaos_project_retrospective）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ClipboardCheck, Lightbulb, BookOpen, Sparkles, TrendingUp } from "lucide-react";

interface RetroItem {
  id: string;
  project: string;
  date: string;
  rating: number;
  done: string;
  improve: string;
  lessons: string;
  actions: number;
  status: "已完成" | "进行中";
}

const INITIAL: RetroItem[] = [
  { id: "r1", project: "XEAOS P0 阶段交付", date: "2026-08-01", rating: 4, done: "晨报/风险雷达/OKR 按时交付，团队协作顺畅", improve: "验收标准前期定义不够细，返工 2 次", lessons: "大模块应先输出接口契约再并行开发", actions: 3, status: "已完成" },
  { id: "r2", project: "AI 营销增长项目", date: "2026-07-28", rating: 3, done: "营销日历与诊断上线，获客提升 30%", improve: "数据接入周期超出预期", lessons: "第三方平台对接需预留缓冲时间", actions: 2, status: "进行中" },
  { id: "r3", project: "知识资产平台", date: "2026-07-20", rating: 5, done: "知识图谱 550+ 篇上线，问答准确率高", improve: "知识质量参差，需加强源头治理", lessons: "知识治理要前置到生产环节", actions: 4, status: "已完成" },
];

const KEY = "xeaos_project_retrospective";

function load(): RetroItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as RetroItem[];
  } catch { /* ignore */ }
  return INITIAL;
}

export default function ProjectRetrospective() {
  const [items, setItems] = useState<RetroItem[]>(load);

  const stats = useMemo(() => {
    const avgRating = items.reduce((a, r) => a + r.rating, 0) / items.length;
    const totalActions = items.reduce((a, r) => a + r.actions, 0);
    const doneCount = items.filter((r) => r.status === "已完成").length;
    return { avgRating, totalActions, doneCount };
  }, [items]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ClipboardCheck size={14} className="text-emerald-400" />
        <h3 className="text-[12px] font-medium text-white">项目复盘与持续改进</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能10 · Retrospective</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="平均复盘评分" value={`${stats.avgRating.toFixed(1)} / 5`} color="#34D399" />
        <MetricCard label="改进行动项" value={`${stats.totalActions} 项`} color="#38BDF8" />
        <MetricCard label="已复盘项目" value={`${stats.doneCount} 个`} color="#A78BFA" />
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={11} className="text-emerald-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">复盘记录 · AI 辅助生成</span>
        </div>
        <div className="space-y-1.5">
          {items.map((r) => (
            <div key={r.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-white">{r.project}</span>
                <span className="text-[9px] font-mono text-gray-600">{r.date}</span>
                <span className="text-[9px] font-mono text-amber-400">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded ${r.status === "已完成" ? "bg-emerald-500/15 text-emerald-400" : "bg-sky-500/15 text-sky-400"}`}>{r.status}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-1.5 text-[9px] font-mono">
                <div className="rounded bg-emerald-500/[0.06] p-1.5"><span className="text-emerald-400">做得好：</span><span className="text-gray-400">{r.done}</span></div>
                <div className="rounded bg-red-500/[0.06] p-1.5"><span className="text-red-400">改进点：</span><span className="text-gray-400">{r.improve}</span></div>
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 text-[9px] font-mono">
                <Lightbulb size={9} className="text-amber-400" />
                <span className="text-gray-400">经验沉淀：{r.lessons}</span>
                <span className="ml-auto text-cyan-400">{r.actions} 项行动已归档至知识库</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen size={11} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">最佳实践库 · 已沉淀模板</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[9px] font-mono">
          {["P0 阶段交付复盘模板", "AI 项目里程碑复盘", "跨部门协作复盘"].map((t, i) => (
            <div key={i} className="flex items-center gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-gray-300">
              <BookOpen size={9} className="text-cyan-400 flex-shrink-0" /> {t}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

